import { useRouteError } from 'react-router';
import { Button } from '@/ui/kit/Button';
import { Spinner } from '@/ui/kit/States';
import styles from './StatusPages.module.css';

import { useT } from '@/i18n/languageStore';

/** Shown while the session is being checked, so a signed-in member never flashes the sign-in page. */
export function Splash() {
  const t = useT();
  return (
    <div className={styles.splash}>
      <span className={styles.brand} aria-hidden="true">
        {t('Kindra')}
      </span>
      <Spinner label={t('Loading Kindra')} />
    </div>
  );
}

/**
 * Something the member cannot see: a deleted post, someone else's
 * conversation, an admin page for a member (spec edge cases, WEB-022).
 */
export function NotAvailablePage({ message }: { message?: string }) {
  const t = useT();
  return (
    <section className={styles.status}>
      <h1 className={styles.title}>{t('Not available')}</h1>
      <p className={styles.text}>
        {message ?? t('This page doesn’t exist any more, or you don’t have access to it.')}
      </p>
      <Button to="/" variant="outline">
        {t('Go to Home')}
      </Button>
    </section>
  );
}

export function NotFoundPage() {
  const t = useT();
  return (
    <section className={styles.status}>
      <h1 className={styles.title}>{t('Page not found')}</h1>
      <p className={styles.text}>{t('The address may be mistyped, or the page has moved.')}</p>
      <Button to="/" variant="outline">
        {t('Go to Home')}
      </Button>
    </section>
  );
}

/** Router error boundary: an unexpected crash on one page never leaves a blank screen. */
export function ErrorPage() {
  const t = useT();
  const error = useRouteError();
  if (import.meta.env.DEV) {
    console.error(error);
  }
  return (
    <section className={styles.status} role="alert">
      <h1 className={styles.title}>{t('Something went wrong')}</h1>
      <p className={styles.text}>
        {t('Please reload the page. If it keeps happening, try again later.')}
      </p>
      <Button onClick={() => window.location.reload()}>{t('Reload')}</Button>
    </section>
  );
}
